import type { PropsWithChildren } from "react";
import clsx from "clsx";
import { GRID_ACTIONS_COLUMN_TYPE, gridClasses, type GridRowParams } from "@mui/x-data-grid";
import Link from "@mui/icons-material/Link";
import LinkOff from "@mui/icons-material/LinkOff";
import type { Entity } from "types";
import { when } from "util/support";
import {
    selectIsOpenBankingResourceLoading,
    selectIsOpenBankingResourceUninitialized,
    selectOpenBankingResourceSlice
} from "features/openBanking/selectors";
import { TabLayout } from "ui/widgets/StatefulTabs";
import Table, {
    useSearchParamsNoop,
    useUserPreferencesNoop
} from "ui/widgets/Table";
import {
    ManageOpenBankingResourcePivotClassNames,
    type OpenBankingResourcePivotActionProps,
    type ManageOpenBankingResourcePivotProps
} from "./types";
import { OpenBankingResourcePivotAction } from "./actions";
import useManageOpenBankingResourcePivot from "./useManageOpenBankingResourcePivot";

const ManageOpenBankingResourcePivot = <
    TResource extends Entity,
    TPivotEntity extends Entity
>({
    menuItem,
    TableProps,
    AssociateActionProps,
    DissassociateActionProps,
    children,
    ...restProps
}: PropsWithChildren<ManageOpenBankingResourcePivotProps<TResource, TPivotEntity>>) => {
    const {
        isPivotsLoading,
        apiRef
    } = useManageOpenBankingResourcePivot(restProps);

    const adapters = {
        useSearchParams: useSearchParamsNoop,
        useUserPreferences: useUserPreferencesNoop
    };

    return (
        <TabLayout>
            <Table
                {...TableProps}
                LayoutProps={{
                    sx: {
                        [`& .${ManageOpenBankingResourcePivotClassNames.PivotActive}`]: {
                            bgcolor: theme => theme.palette.error.light,
                            ':hover': {
                                bgcolor: theme => theme.palette.error.light
                            },
                            [`&.${gridClasses.row}.Mui-hovered`]: {
                                bgcolor: theme => theme.palette.error.light
                            }
                        }
                    }
                }}
                FiltersProps={{
                    adapters,
                    sx: {
                        mt: 0
                    }
                }}
                selectIsTableUninitialized={selectIsOpenBankingResourceUninitialized(menuItem)}
                selectIsTableLoading={selectIsOpenBankingResourceLoading(menuItem)}
                selectTableDataSlice={selectOpenBankingResourceSlice(menuItem)}
                adapters={adapters}
                initialState={{
                    pinnedColumns: {
                        right: [GRID_ACTIONS_COLUMN_TYPE]
                    }
                }}
                getRowClassName={({ id }) => clsx(
                    apiRef.current.pivots.has(id) &&
                    ManageOpenBankingResourcePivotClassNames.PivotActive
                )}
                columns={[
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        getActions: (params: GridRowParams) => {
                            const rowId = params.id;
                            const { pivots } = apiRef.current;
                            const commonProps = {
                                ...params,
                                isLoading: isPivotsLoading,
                                pivotId: apiRef.current.getPivotByResourceId(rowId)?.pki
                            };

                            return [
                                <OpenBankingResourcePivotAction
                                    {...when(
                                        pivots.has(rowId),
                                        (): OpenBankingResourcePivotActionProps<TPivotEntity> => ({
                                            ...commonProps,
                                            getOpenBankingResourcePivotActionProps: params => {
                                                const actionProps = DissassociateActionProps.getOpenBankingResourcePivotActionProps(params);

                                                return {
                                                    ...actionProps,
                                                    onSuccess: () => apiRef.current.deletePivotResourceId(rowId),
                                                    TriggerComponentProps: {
                                                        color: 'error',
                                                        label: 'Dissassociate',
                                                        icon: <LinkOff />,
                                                        ...actionProps.TriggerComponentProps,
                                                        TooltipProps: {
                                                            title: `Delete pivot ID ${params.pivotId}`,
                                                            ...actionProps.TriggerComponentProps.TooltipProps
                                                        }
                                                    },
                                                    ActionPanelProps: {
                                                        ...actionProps.ActionPanelProps,
                                                        SaveActionProps: {
                                                            color: 'error',
                                                            children: 'Delete',
                                                            ...actionProps.ActionPanelProps.SaveActionProps
                                                        }
                                                    }
                                                }
                                            }
                                        }),
                                        (): OpenBankingResourcePivotActionProps<TPivotEntity> => ({
                                            ...commonProps,
                                            getOpenBankingResourcePivotActionProps: params => {
                                                const actionProps = AssociateActionProps.getOpenBankingResourcePivotActionProps(params);

                                                return {
                                                    ...actionProps,
                                                    onSuccess: pivot => apiRef.current.setPivot(rowId, pivot as TPivotEntity),
                                                    TriggerComponentProps: {
                                                        color: 'success',
                                                        label: 'Associate',
                                                        icon: <Link />,
                                                        ...actionProps.TriggerComponentProps,
                                                        TooltipProps: {
                                                            title: `Associate resource ID ${params.id}`,
                                                            ...actionProps.TriggerComponentProps.TooltipProps
                                                        }
                                                    },
                                                    ActionPanelProps: {
                                                        ...actionProps.ActionPanelProps,
                                                        SaveActionProps: {
                                                            children: 'Create',
                                                            ...actionProps.ActionPanelProps.SaveActionProps
                                                        }
                                                    }
                                                }
                                            }
                                        })
                                    )}
                                >
                                    {children}
                                </OpenBankingResourcePivotAction>
                            ];
                        }
                    }
                ]}
            />
        </TabLayout>
    );
};

export default ManageOpenBankingResourcePivot;
